import React from 'react';
import { Info, Building, FileText, PencilRuler, Users, Calendar, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией об архитектурном проектировании и ценообразовании
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об архитектурном проектировании: этапы, стоимость, сроки
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Building className="w-5 h-5 text-indigo-500" />
          Что такое архитектурное проектирование?
        </h3>
        <p>
          <strong>Архитектурное проектирование</strong> — это творческий процесс создания концепции и детальной документации для строительства здания или сооружения. Включает разработку планировочных, функциональных, конструктивных, инженерных и эстетических решений, соответствующих техническому заданию заказчика и нормативным требованиям.
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Этапы архитектурного проектирования
        </h3>
        <p>
          Современное архитектурное проектирование — это многоступенчатый процесс, который обычно включает следующие основные этапы:
        </p>
        <ol>
          <li>
            <strong>Предпроектная подготовка</strong> — сбор исходных данных, изучение участка, анализ нормативных требований, составление технического задания.
          </li>
          <li>
            <strong>Эскизный проект (Концепция)</strong> — разработка общей архитектурной идеи, объемно-планировочных решений, визуализаций. Это первый этап творческой работы, определяющий облик будущего объекта.
          </li>
          <li>
            <strong>Проектная документация</strong> — подробная разработка архитектурных, конструктивных и инженерных решений, необходимых для получения разрешения на строительство и прохождения экспертизы.
          </li>
          <li>
            <strong>Рабочая документация</strong> — детальная проработка всех решений с выпуском чертежей, спецификаций и ведомостей для строительства объекта.
          </li>
          <li>
            <strong>Авторский надзор</strong> — контроль реализации проекта на строительной площадке, внесение необходимых корректировок, консультации строителей.
          </li>
        </ol>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Состав проектной документации</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Проектная документация включает разделы: "Пояснительная записка", "Схема планировочной организации участка", "Архитектурные решения", "Конструктивные решения", "Инженерные системы", "Проект организации строительства", "Мероприятия по охране окружающей среды" и др.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PencilRuler className="w-5 h-5 text-indigo-500" />
          Факторы, влияющие на стоимость проектирования
        </h3>
        <p>
          Ценообразование в архитектурном проектировании зависит от множества факторов:
        </p>
        <ul>
          <li>
            <strong>Сложность объекта</strong> — нестандартные планировки, сложные конструктивные решения, уникальные фасады значительно повышают стоимость проектирования.
          </li>
          <li>
            <strong>Площадь и этажность</strong> — основные физические параметры, влияющие на объем работы и стоимость. Часто используется базовая ставка за квадратный метр, которая корректируется другими факторами.
          </li>
          <li>
            <strong>Тип объекта</strong> — жилые, общественные, промышленные, специализированные здания требуют разного уровня детализации и экспертизы.
          </li>
          <li>
            <strong>Уровень проработки</strong> — чем детальнее документация, тем выше стоимость. Эскизный проект стоит значительно дешевле полного комплекта рабочей документации.
          </li>
          <li>
            <strong>Состав проекта</strong> — включение дополнительных разделов (интерьеры, ландшафт, специальные инженерные системы) увеличивает стоимость.
          </li>
          <li>
            <strong>Срочность выполнения</strong> — ускоренные сроки обычно повышают стоимость проектирования на 20-80%.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-500" />
          Кто участвует в разработке архитектурного проекта?
        </h3>
        <p>
          Над комплексным архитектурным проектом обычно работает команда специалистов:
        </p>
        <ul>
          <li><strong>Главный архитектор проекта (ГАП)</strong> — руководит разработкой архитектурных и планировочных решений, отвечает за общую концепцию.</li>
          <li><strong>Главный инженер проекта (ГИП)</strong> — координирует инженерные разделы и взаимодействие между смежными специалистами.</li>
          <li><strong>Архитекторы</strong> — разрабатывают планировочные решения, фасады, детали и спецификации.</li>
          <li><strong>Конструкторы</strong> — создают конструктивные решения и расчеты для несущих систем здания.</li>
          <li><strong>Инженеры</strong> различных специальностей — проектируют отопление, вентиляцию, водоснабжение, электроснабжение и другие системы.</li>
          <li><strong>Визуализаторы и 3D-моделисты</strong> — создают наглядные изображения и модели проекта.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Типичные ошибки при заказе проектирования
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Нечеткое техническое задание, приводящее к многочисленным изменениям</li>
            <li>Недооценка сроков проектирования и согласований</li>
            <li>Выбор проектировщика только по минимальной цене</li>
            <li>Игнорирование необходимости инженерных изысканий</li>
            <li>Отказ от авторского надзора, приводящий к ошибкам при строительстве</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-500" />
          Сроки архитектурного проектирования
        </h3>
        <p>
          Сроки проектирования зависят от масштаба и сложности объекта, но можно ориентироваться на следующие временные рамки:
        </p>
        <ul>
          <li><strong>Эскизный проект</strong> небольшого объекта: 2-4 недели</li>
          <li><strong>Проектная документация</strong> жилого дома: 1,5-3 месяца</li>
          <li><strong>Рабочая документация</strong> жилого дома: 2-4 месяца</li>
          <li><strong>Полный комплект документации</strong> для общественного здания: 6-12 месяцев</li>
          <li><strong>Согласования и экспертиза</strong>: 1-3 месяца дополнительно</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-500" />
          Как выбрать архитектора или проектное бюро?
        </h3>
        <ol>
          <li><strong>Изучите портфолио</strong> реализованных проектов, похожих на ваш по типу и масштабу.</li>
          <li><strong>Запросите рекомендации</strong> от предыдущих клиентов или посетите построенные объекты.</li>
          <li><strong>Проверьте наличие</strong> необходимых лицензий, сертификатов, членства в СРО.</li>
          <li><strong>Обсудите подход к работе</strong>, чтобы убедиться, что вы "на одной волне" с проектировщиком.</li>
          <li><strong>Сравните предложения</strong> нескольких бюро по составу работ, срокам и стоимости.</li>
          <li><strong>Запросите детальное техническое задание</strong> и договор с четким описанием этапов, сроков и результатов.</li>
        </ol>

        <h3>Преимущества профессионального архитектурного проектирования</h3>
        <ul>
          <li><strong>Оптимизация пространства</strong> для максимально эффективного использования площадей.</li>
          <li><strong>Экономия при строительстве</strong> за счет продуманных инженерных и конструктивных решений.</li>
          <li><strong>Снижение эксплуатационных расходов</strong> благодаря энергоэффективным решениям.</li>
          <li><strong>Увеличение стоимости объекта</strong> за счет качественной архитектуры и продуманных планировок.</li>
          <li><strong>Минимизация ошибок</strong> при строительстве и связанных с ними дополнительных затрат.</li>
          <li><strong>Юридическая защита</strong> при возникновении спорных ситуаций в процессе строительства.</li>
        </ul>

        <p className="font-semibold mt-6">
          Архитектурное проектирование — это инвестиция в качество, безопасность и долговечность вашего объекта. Наш калькулятор поможет вам сориентироваться в стоимости проектных работ и составить предварительный бюджет!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;